import React from "react";
import ReactTooltip from "react-tooltip";
import { useLocation } from "react-router";

import CommonDropDown from "./DropDown/CommonDropDown";
import TextBox from "./TextBox";

import ImagerApi from "../../api/ImagerApi";

import { ImagerState } from "../../Model/ModuleState";

import "./ServicingTable.css";

function getHeaderDrop(
  currentDrop: number,
  maxDrop: number,
  onChangeDrop: (drop: number) => void
) {
  const options: number[] = [];
  for (let i = 0; i < maxDrop; i++) {
    options.push(i);
  }
  return (
    <CommonDropDown
      id="drop-table-selector"
      value={currentDrop}
      style={{
        height: "32px",
        margin: 5,
        borderRadius: "0px 6px 0px 0px",
        fontSize: 13.3,
        padding: "0px 3px 0px 3px",
      }}
      onChange={(value) => onChangeDrop(value)}
      dropDownDisplayValues={options.map((option) => ({
        display: `Drop ${option + 1}`,
        value: option,
        disabled: false,
      }))}
    />
  );
}

export interface ServicingTableRow {
  id: string;
  name: string;
  x: number;
  y: number;
  z: number;
  selected: boolean;
  tooltip?: string;
}

type Props = {
  imagerState: ImagerState;
  imagerIdentifier: string;
  data: ServicingTableRow[];
  onRowSelected: (row: ServicingTableRow) => void;
  dropNumber: number;
  maxDrop: number;
  onDropNumberChanged: (drop: number) => void;
  onRowUpdated: (row: ServicingTableRow) => void;
  style?: React.CSSProperties;
  disabled?: boolean;
  actionLabel?: string;
};

export default function ServicingTable({
  imagerState,
  imagerIdentifier,
  data,
  onRowSelected,
  dropNumber,
  maxDrop,
  onDropNumberChanged,
  onRowUpdated,
  style,
  disabled = false,
  actionLabel = "Move",
}: Props) {
  const { pathname } = useLocation();

  // this limit used currently just for drop position
  const isPlateTypeEditorCalibrationPage =
    pathname === "/plate-type-editor/calibration";

  const tableRow = data.map((row, index) => {
    const isXError = isPlateTypeEditorCalibrationPage
      ? row?.x < imagerState.minPosition?.x || row?.x > imagerState.maxPosition?.x
      : false;
    const isYError = isPlateTypeEditorCalibrationPage
      ? row?.y < imagerState.minPosition?.y || row?.y > imagerState.maxPosition?.y
      : false;
    const isZError = isPlateTypeEditorCalibrationPage
      ? row?.z < imagerState.minPosition?.z || row?.z > imagerState.maxPosition?.z
      : false;

    const isError = isXError || isYError || isZError;

    return (
      <tr key={`servicing-table-row-${index}`}>
        <td onClick={() => onRowSelected(row)}>
          <div title={row?.tooltip} className="servicing-table-name-column">
            <svg
              width="23"
              height="17"
              viewBox="0 0 23 17"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0)">
                <path
                  d="M7.77051 0.625L19.0469 8.5L7.77051 16.375V0.625Z"
                  fill={row.selected && !disabled ? "#E38006" : "#828282"}
                />
              </g>
              <defs>
                <clipPath id="clip0">
                  <rect
                    width="22.5527"
                    height="15.75"
                    fill="white"
                    transform="translate(0.25293 0.625)"
                  />
                </clipPath>
              </defs>
            </svg>
            {row.name}
          </div>
        </td>
        <td
          data-for="servicing-tooltip"
          data-tip={
            isXError
              ? `Value is exceeding limit. Please enter value between ${imagerState.minPosition.x} and ${imagerState.maxPosition.x}.`
              : ""
          }
        >
          <TextBox
            className={`servicing-table-input ${isXError && "error"}`}
            disabled={disabled}
            name={"X"}
            isInteger={false}
            variant="number"
            value={+row.x.toFixed(2)}
            readOnly={!row.selected}
            onClick={() => onRowSelected(row)}
            onInput={(e: any) => {
              onRowUpdated({
                ...row,
                x: +e.target.value,
              });
            }}
          />
        </td>
        <td
          data-for="servicing-tooltip"
          data-tip={
            isYError
              ? `Value is exceeding limit. Please enter value between ${imagerState.minPosition.y} and ${imagerState.maxPosition.y}.`
              : ""
          }
        >
          <TextBox
            className={`servicing-table-input ${isYError && "error"}`}
            disabled={disabled}
            name={"Y"}
            variant="number"
            isInteger={false}
            value={+row.y.toFixed(2)}
            readOnly={!row.selected}
            onClick={() => onRowSelected(row)}
            onInput={(e: any) => {
              onRowUpdated({
                ...row,
                y: +e.target.value,
              });
            }}
          />
        </td>
        <td
          data-for="servicing-tooltip"
          data-tip={
            isZError
              ? `Value is exceeding limit. Please enter value between ${imagerState.minPosition.z} and ${imagerState.maxPosition.z}.`
              : ""
          }
        >
          <TextBox
            className={`servicing-table-input ${isZError && "error"}`}
            disabled={disabled}
            name={"Z"}
            variant="number"
            isInteger={false}
            value={+row.z.toFixed(2)}
            readOnly={!row.selected}
            onClick={() => onRowSelected(row)}
            onInput={(e: any) => {
              onRowUpdated({
                ...row,
                z: +e.target.value,
              });
            }}
          />
        </td>
        <td>
          <button
            className="servicing-table-button"
            onClick={() =>
              onRowUpdated({
                ...row,
                x: imagerState.imagingArmPosition.x,
                y: imagerState.imagingArmPosition.y,
                z: imagerState.imagingArmPosition.z,
              })
            }
            disabled={!row.selected || disabled}
          >
            INPUT
          </button>
        </td>
        <td>
          <button
            className="servicing-table-button"
            onClick={() => ImagerApi.armMoveAbsolute(imagerIdentifier, row)}
            disabled={!row.selected || disabled || isError}
          >
            MOVE
          </button>
        </td>
      </tr>
    );
  });

  return (
    <div style={{ ...style }}>
      <table
        className={
          disabled
            ? "servicing-table servicing-table-disabled"
            : "servicing-table"
        }
      >
        <thead>
          <tr>
            <th>Name</th>
            <th>Position X</th>
            <th>Position Y</th>
            <th>Position Z</th>
            <th>
              {maxDrop > 1
                ? getHeaderDrop(dropNumber, maxDrop, onDropNumberChanged)
                : ""}
            </th>
            <th>{actionLabel}</th>
          </tr>
        </thead>
        <tbody>{tableRow}</tbody>
      </table>
      <ReactTooltip
        id="servicing-tooltip"
        place="bottom"
        effect="solid"
        className="servicing-table-tooltip"
      />
    </div>
  );
}
